import React, { useState, useEffect, useCallback } from 'react';
import { HiX } from 'react-icons/hi';
import CustomButton from '../../components/common/CustomButton';
import FormInput from '../../components/common/FormInput';
import useViewsService from '../../services/views';
import { SaveViewModalProps } from './SavedViews.def';
import './SaveViewModal.css';

/* Used when captured state section is shown
const FILTER_LABELS: Record<string, string> = {
  type: 'Type',
  status: 'Status',
  source: 'Source',
  sort: 'Sort',
  layout: 'Layout',
  quarter: 'Quarter',
  pinned: 'Pinned',
};

const formatFilterValue = (values: string[]): string => {
  if (!values || values.length === 0) return 'All';
  return values.join(', ');
};
*/

const SaveViewModal: React.FC<SaveViewModalProps> = ({
  isVisible,
  component,
  editingView,
  currentFilters,
  onClose,
  onSuccess,
}) => {
  const [viewName, setViewName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createView, updateView } = useViewsService();

  const isEditMode = !!editingView;

  // Pre-fill fields when editing
  useEffect(() => {
    if (editingView) {
      setViewName(editingView.name || '');
      setDescription(editingView.description || '');
    } else {
      setViewName('');
      setDescription('');
    }
  }, [editingView]);

  // Focus View Name input when modal opens
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => document.getElementById('save-view-name')?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleSave = useCallback(async () => {
    if (!viewName.trim()) return;

    setIsSubmitting(true);
    try {
      if (isEditMode && editingView) {
        await updateView(editingView.id, {
          name: viewName.trim(),
          description: description.trim() || undefined,
        });
      } else {
        await createView({
          name: viewName.trim(),
          description: description.trim() || undefined,
          component,
          config: currentFilters || {},
        });
      }
      setViewName('');
      setDescription('');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save view:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [viewName, description, isEditMode, editingView, component, currentFilters, createView, updateView, onSuccess, onClose]);

  // Keyboard: Escape to close, Enter to save
  useEffect(() => {
    if (!isVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && viewName.trim()) handleSave();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isVisible, viewName, handleSave, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className="save-view-overlay"
      onClick={onClose}
    >
      <div
        className="save-view-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="save-view-header">
          <div>
            <h2 className="save-view-title">
              {isEditMode ? 'Edit View' : 'Save Current View'}
            </h2>
            <p className="save-view-subtitle">
              {isEditMode
                ? 'Update the name and description of this saved view.'
                : 'Save your current filters, sort order, and layout as a reusable view.'}
            </p>
          </div>
          <button
            className="save-view-close-btn"
            onClick={onClose}
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="save-view-body">
          <FormInput
            id="save-view-name"
            name="viewName"
            label="View Name"
            type="text"
            value={viewName}
            placeholder="e.g. My Active Epics"
            onChange={(e) => setViewName(e.target.value)}
            required
            showAsterisk
          />
          <FormInput
            id="save-view-description"
            name="description"
            label="Description (optional)"
            type="textarea"
            value={description}
            placeholder="Describe what this view shows..."
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Captured state summary - hidden from user for now */}
          {/* {((!isEditMode && currentFilters && Object.keys(currentFilters).length > 0) ||
            (isEditMode && editingView?.config && Object.keys(editingView.config).length > 0)) && (
            <div className="save-view-captured">
              {isEditMode ? (
                <div style={{ marginBottom: '8px' }}>
                  <span className="save-view-captured-title">Saved configuration:</span>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', marginTop: '4px', lineHeight: '1.25' }}>
                    Note: The filter configuration below is fixed for this view. You can only edit the name and description.
                  </p>
                </div>
              ) : (
                <span className="save-view-captured-title">Captured state:</span>
              )}
              {Object.entries(isEditMode ? editingView!.config : currentFilters!).map(([key, values]) => (
                <div key={key} className="save-view-captured-row">
                  <span className="save-view-captured-key">{FILTER_LABELS[key] || key}:</span>
                  <span className="save-view-captured-value">{formatFilterValue(values)}</span>
                </div>
              ))}
            </div>
          )} */}
        </div>

        {/* Footer */}
        <div className="save-view-footer">
          <CustomButton
            variant="cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleSave}
            disabled={!viewName.trim() || isSubmitting}
            isLoading={isSubmitting}
          >
            {isEditMode ? 'Update View' : 'Save View'}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default SaveViewModal;
